import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SharedService {

  private readonly _sort$ = new BehaviorSubject<any>({});
  public readonly sort$: Observable<any> = this._sort$.asObservable();

  private readonly _color$ = new BehaviorSubject<any>([]);
  public readonly color$: Observable<any> = this._color$.asObservable();

  private readonly _isListView$ = new BehaviorSubject<any>(true);
  public readonly listView$: Observable<any> = this._isListView$.asObservable();

  private readonly _selectedTimeFrame$ = new BehaviorSubject<any>({});
  public readonly selectedTimeFrame$: Observable<any> = this._selectedTimeFrame$.asObservable();

  private readonly _isEnabledSearch$ = new BehaviorSubject(false);
  public readonly isEnabledSearch$ = this._isEnabledSearch$.asObservable();

  private readonly _dashboardList$ = new BehaviorSubject([]);
  public readonly dashboardList$ = this._dashboardList$.asObservable();

  private readonly _timeout$ = new BehaviorSubject(false);
  public readonly timeout$ = this._timeout$.asObservable();

  // Comments read sync across modules
  private readonly _commentsRead$ = new Subject<number>();
  public readonly commentsRead$ = this._commentsRead$.asObservable();

  private readonly _selectedFilters$ = new BehaviorSubject<any>({
    state: [],
    retailer_group: [],
    retailers: [],
    delivery_zones: [],
  });
  public readonly selectedFilters$: Observable<any> = this._selectedFilters$.asObservable();

  // Late Orders specific observables
  private readonly _lateOrdersSelectedFilters$ = new BehaviorSubject<any>({
    state: [],
    retailer_group: [],
    retailers: [],
    delivery_zones: [],
  });
  public readonly lateOrdersSelectedFilters$: Observable<any> = this._lateOrdersSelectedFilters$.asObservable();

  private readonly _lateOrdersSelectedTimeFrame$ = new BehaviorSubject<any>({});
  public readonly lateOrdersSelectedTimeFrame$: Observable<any> = this._lateOrdersSelectedTimeFrame$.asObservable();

  constructor() {
  }

  setSort(item: any) {
    this._sort$.next(item)
  }

  setSelectedFilters(item: any) {
    this._selectedFilters$.next(item)
  }

  // Late Orders specific methods
  setLateOrdersSelectedFilters(item: any) {
    this._lateOrdersSelectedFilters$.next(item)
  }

  setLateOrdersSelectedTimeFrame(item: any) {
    this._lateOrdersSelectedTimeFrame$.next(item)
  }

  setColor(order: any) {
    const current = this._color$.value;
    const index = current.findIndex((x: any) => x.order_id == order.order_id);
    const temp = index >= 0
      ? current.map((item: any, i: number) => (i === index ? { ...item, ...order } : item))
      : [...current, order];
    this._color$.next(temp);
  }

  setAllColors(orders: any) {
    this._color$.next(orders)
  }

  setView(view: any) {
    this._isListView$.next(view)
  }

  resetColor() {
    this._color$.next([])
  }

  setSelectedTimeFrame(item: any) {
    this._selectedTimeFrame$.next(item)
  }

  setIsSearchEnabled(view:boolean){
    this._isEnabledSearch$.next(view);
  }

  setRowDashboardData(row:any){
    this._dashboardList$.next(row);
  }

  setTimeout(value:any){
    this._timeout$.next(value);
  }

  // Notify listeners that comments for orderId are read
  markCommentsRead(orderId: number) {
    if (typeof orderId === 'number') {
      this._commentsRead$.next(orderId);
    }
  }

  // Method to safely load external images and prevent CORB errors
  getSafeImageUrl(url: string): string {
    if (!url) return '';
    
    // If it's already a data URL or relative path, return as is
    if (url.startsWith('data:') || url.startsWith('./') || url.startsWith('/')) {
      return url;
    }
    
    // For external URLs, add a proxy or return empty to prevent CORB
    // You can implement a proxy here if needed
    try {
      // For now, return the URL but it will be handled by error handling in the component
      return url;
    } catch (error) {
      return '';
    }
  }

  // Method to check if image URL is accessible
  async isImageAccessible(url: string): Promise<boolean> {
    if (!url) return false;
    
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }

  hasAnyRole(roleIds: number[]): boolean {
    try {
      const raw = localStorage.getItem(environment.storage.userData);
      if (!raw) return false;
      const user = JSON.parse(raw);
      const rolesArr = Array.isArray(user?.roles) ? user.roles : [];
      return rolesArr.some((r: any) => roleIds.includes(Number(r?.id)));
    } catch {
      return false;
    }
  }
}
